import { useEffect, useRef } from 'react';

// Optimized: no mix-blend-mode (was causing text flicker), pre-drawn sprite for
// particle draw (no per-frame radial gradient creation), lower count, and
// respects prefers-reduced-motion.
export default function GoldParticles() {
  const canvasRef = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    // Skip animation entirely for reduced-motion users
    if (window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      return;
    }

    const ctx = canvas.getContext('2d', { alpha: true });
    let animationFrameId;
    let particles = [];
    let running = true;

    const dpr = Math.min(window.devicePixelRatio || 1, 1.5);
    const setCanvasSize = () => {
      const w = window.innerWidth;
      const h = window.innerHeight;
      canvas.width = w * dpr;
      canvas.height = h * dpr;
      canvas.style.width = w + 'px';
      canvas.style.height = h + 'px';
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    };
    setCanvasSize();
    window.addEventListener('resize', setCanvasSize);

    // Pre-render a single golden particle sprite ONCE — drawImage is far cheaper
    // than createRadialGradient per particle per frame.
    const SPRITE_SIZE = 24;
    const sprite = document.createElement('canvas');
    sprite.width = sprite.height = SPRITE_SIZE;
    const sctx = sprite.getContext('2d');
    const g = sctx.createRadialGradient(SPRITE_SIZE / 2, SPRITE_SIZE / 2, 0, SPRITE_SIZE / 2, SPRITE_SIZE / 2, SPRITE_SIZE / 2);
    g.addColorStop(0, 'rgba(255, 215, 0, 1)');
    g.addColorStop(0.5, 'rgba(212, 175, 55, 0.7)');
    g.addColorStop(1, 'rgba(212, 175, 55, 0)');
    sctx.fillStyle = g;
    sctx.fillRect(0, 0, SPRITE_SIZE, SPRITE_SIZE);

    class Particle {
      constructor() { this.reset(); this.y = Math.random() * window.innerHeight; }
      reset() {
        this.x = Math.random() * window.innerWidth;
        this.y = -10;
        this.speed = Math.random() * 0.6 + 0.3;
        this.size = (Math.random() * 3 + 2) * 2; // draw diameter
        this.opacity = Math.random() * 0.4 + 0.2;
        this.swing = Math.random() * 1.4 - 0.7;
        this.swingSpeed = Math.random() * 0.008 + 0.004;
        this.angle = Math.random() * Math.PI * 2;
      }
      update() {
        this.y += this.speed;
        this.angle += this.swingSpeed;
        this.x += Math.sin(this.angle) * this.swing;
        if (this.y > window.innerHeight + 10) this.reset();
      }
      draw() {
        ctx.globalAlpha = this.opacity;
        ctx.drawImage(sprite, this.x - this.size / 2, this.y - this.size / 2, this.size, this.size);
      }
    }

    // Reduce particle count for perf
    const particleCount = 60;
    for (let i = 0; i < particleCount; i++) particles.push(new Particle());

    // Pause when tab hidden to save CPU
    const onVisibility = () => {
      running = !document.hidden;
      if (running) animate();
    };
    document.addEventListener('visibilitychange', onVisibility);

    const animate = () => {
      if (!running) return;
      ctx.clearRect(0, 0, window.innerWidth, window.innerHeight);
      ctx.globalAlpha = 1;
      for (let i = 0; i < particles.length; i++) {
        particles[i].update();
        particles[i].draw();
      }
      animationFrameId = requestAnimationFrame(animate);
    };
    animate();

    return () => {
      cancelAnimationFrame(animationFrameId);
      window.removeEventListener('resize', setCanvasSize);
      document.removeEventListener('visibilitychange', onVisibility);
      running = false;
    };
  }, []);

  return (
    <canvas
      ref={canvasRef}
      className="fixed inset-0 pointer-events-none z-[1]"
      aria-hidden="true"
    />
  );
}
